import axios from 'axios';
import { API_URL } from '../api/config';

const API_BASE_URL = API_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {

    if (error.response?.status === 401) {
      // Don't logout on password change errors (wrong current password)
      if (error.config?.url?.includes('/change-password')) {
        return Promise.reject(error);
      }

      // Token expired or invalid - logout and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  verify: () => api.get('/auth/verify'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.put('/auth/change-password', data)
};

// Users API
export const usersAPI = {
  getUsers: () => api.get('/users'),
  getUsersByLocation: (locationId) => api.get(`/users/location/${locationId}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),
  updateUserStatus: (userId, status) => api.put(`/users/${userId}/status`, status),
  updateUserLocation: (userId, locationData) => api.put(`/users/${userId}/location`, locationData),
  getUserDetails: (userId) => api.get(`/users/${userId}`),
  // Owner location management
  getOwnerLocations: () => api.get('/users/locations'),
  getOwnerLocation: (locationId) => api.get(`/users/locations/${locationId}`),
  updateOwnerLocation: (locationId, locationData) => api.put(`/users/locations/${locationId}`, locationData)
};

// Locations API
export const locationsAPI = {
  getLocations: () => api.get('/locations'),
  getLocationDetails: (locationId) => api.get(`/locations/${locationId}`),
  createLocation: (locationData) => api.post('/locations', locationData),
  updateLocation: (locationId, locationData) => api.put(`/locations/${locationId}`, locationData),
  getLocationServices: (locationId) => api.get(`/locations/${locationId}/services`),
  addLocationService: (locationId, serviceData) => api.post(`/locations/${locationId}/services`, serviceData)
};

// Vehicles API
export const vehiclesAPI = {
  checkinVehicle: (vehicleData) => api.post('/vehicles/checkin', vehicleData),
  getActiveVehicles: (locationId) => api.get(`/vehicles/${locationId}/active`),
  getDeliveredVehicles: (locationId) => api.get(`/vehicles/${locationId}/delivered`),
  updateVehicleStatus: (transactionId, statusData) => api.put(`/vehicles/${transactionId}/status`, statusData),
  callVehicle: (transactionId) => api.post(`/vehicles/${transactionId}/call`),
  getVehicleByQR: (qrToken) => api.get(`/vehicles/qr/${qrToken}`),
  searchByPlate: (plateNumber) => api.get(`/vehicles/search/plate/${plateNumber}`),
  searchByPlateInLocation: (plateNumber, locationId) => api.get(`/vehicles/search/plate/${plateNumber}/location/${locationId}`),
  searchByTransactionId: (transactionId) => api.get(`/vehicles/search/transaction/${transactionId}`),
  searchByReference: (referenceCode) => api.get(`/vehicles/search/reference/${referenceCode}`),
  searchDeliveredVehicles: (identifier) => api.get(`/vehicles/search/delivered/${identifier}`),
  undoDelivery: (transactionId, data) => api.post(`/vehicles/${transactionId}/undo-delivery`, data)
};

// Reports API
export const reportsAPI = {
  getDailyReport: (params) => api.get('/reports/daily', { params }),
  getVehicleHistory: (params) => api.get('/reports/vehicle-history', { params }),
  getRevenueReport: (params) => api.get('/reports/revenue', { params }),
  getStaffPerformance: (params) => api.get('/reports/staff-performance', { params }),
  searchTransactions: (params) => api.get('/reports/search', { params })
};

// SMS API
export const smsAPI = {
  sendSMS: (smsData) => api.post('/sms/send', smsData),
  getSMSLogs: (transactionId) => api.get(`/sms/logs/${transactionId}`),
  getSMSStats: (locationId) => api.get(`/sms/stats/${locationId}`)
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getLocations: (filter = 'all', page = 1, limit = 20, search = '', type = 'all') => api.get('/admin/locations', {
    params: { filter, page, limit, search, type }
  }),
  getUsers: () => api.get('/admin/users'),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  getLocationStats: (locationId) => api.get(`/admin/locations/${locationId}/stats`),

  // User management
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  // Location management
  createLocation: (locationData) => api.post('/admin/locations', locationData),
  updateLocation: (locationId, locationData) => api.put(`/admin/locations/${locationId}`, locationData),
  updateLocationStatus: (locationId, status) => api.patch(`/admin/locations/${locationId}/status`, { status }),
  deleteLocation: (locationId) => api.delete(`/admin/locations/${locationId}`),
  assignLocationOwner: (locationId, ownerId) => api.post(`/admin/locations/${locationId}/assign-owner`, { owner_id: ownerId }),
  removeLocationOwner: (locationId, ownerId) => api.delete(`/admin/locations/${locationId}/remove-owner`, { data: { owner_id: ownerId } }),

  // SMS Credit
  getSMSCredit: () => api.get('/admin/sms-credit'),

  // Contact Messages & Applications
  getContactMessages: (status, limit = 50, offset = 0) => api.get('/admin/contact-messages', { params: { status, limit, offset } }),
  getApplications: (status, limit = 50, offset = 0) => api.get('/admin/applications', { params: { status, limit, offset } }),
  deleteContactMessage: (id) => api.delete(`/admin/contact-messages/${id}`),
  deleteApplication: (id) => api.delete(`/admin/applications/${id}`),

  // API Token Management
  getAPITokens: () => api.get('/admin/api-tokens'),
  createAPIToken: (tokenData) => api.post('/admin/api-tokens', tokenData),
  updateAPIToken: (tokenId, tokenData) => api.put(`/admin/api-tokens/${tokenId}`, tokenData),
  deleteAPIToken: (tokenId) => api.delete(`/admin/api-tokens/${tokenId}`)
};

// Helper functions
export const handleApiError = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const isNetworkError = (error) => {
  return !error.response && error.request;
};

// Export the main api instance for custom calls
export default api;